import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Music2, Sparkles } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import PublicNavbar from "../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGES = [
  {
    id: "starter_boost",
    name: "Starter Boost",
    price: 49,
    duration_days: 7,
    description: "Promotion + traffic campaign to increase plays/streams and engagement.",
  },
  {
    id: "growth_campaign",
    name: "Growth Campaign",
    price: 99,
    duration_days: 14,
    description: "Higher reach campaign designed to increase clicks, followers, and momentum.",
  },
  {
    id: "premium_push",
    name: "Premium Push",
    price: 199,
    duration_days: 30,
    description: "Full-month push for sustained traffic, reach, and conversion tracking.",
  },
];

const PromoBoostPortal = () => {
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState("growth_campaign");

  const [form, setForm] = useState({
    artist_name: "",
    title: "",
    email: "",
    phone: "",
    primary_link: "",
    spotify: "",
    apple_music: "",
    youtube_video: "",
    youtube_channel: "",
    soundcloud: "",
    audiomack: "",
    tidal: "",
    amazon_music: "",
    deezer: "",
    bandcamp: "",
    pandora: "",
    genre: "",
    mood: "",
    target_audience: "",
    location: "",
    notes: "",
  });

  const selectedPackage = useMemo(
    () => PACKAGES.find((p) => p.id === selectedPackageId),
    [selectedPackageId]
  );

  const update = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleCheckout = async () => {
    if (!form.artist_name || !form.title || !form.email || !form.phone || !form.primary_link) {
      toast.error("Please fill: Artist name, Title, Email, Phone, and Primary link.");
      return;
    }

    if (!selectedPackage) {
      toast.error("Please select a package.");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/promo/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: selectedPackage.id,
          origin_url: window.location.origin,
          intake: form,
        }),
      });

      if (!resp.ok) {
        const errText = await resp.text();
        let errJson = {};
        try {
          errJson = JSON.parse(errText);
        } catch {
          // ignore parse error
        }
        toast.error(errJson.detail || errJson.error || errText || "Failed to start checkout");
        return;
      }

      const data = await resp.json();
      if (!data?.url) {
        toast.error("No checkout URL returned");
        return;
      }

      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
      <PublicNavbar />

      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <section className="hero pt-20" data-testid="promo-hero">
        <div className="hero-bg-reflection" aria-hidden>
          <div className="hero-bg-reflection-text">
            <img src="/logo-reflection-trimmed.png" alt="" className="hero-bg-reflection-logo" />
          </div>
        </div>

        <div className="hero-inner">
          <div className="hero-logo" style={{ animationDelay: "0.05s" }}>
            <img src="/logo-reflection-trimmed.png" alt="DopeInHD" className="hero-logo-img" />
          </div>

          <p className="hero-subtitle" style={{ animationDelay: "0.15s" }}>
            Promo Boost Portal — promotion + traffic campaigns to increase plays/streams
          </p>
        </div>
      </section>

      <div className="relative z-10 px-6 pb-16 w-full max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Intake */}
          <Card className="lg:col-span-2 bg-[#121214] border-[#27272A]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-[#FAFAFA]">Music Link Intake</h2>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    Submit your links and goals. No guaranteed streams — we focus on measurable clicks, reach, and conversions.
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-2 text-xs text-[#71717A]">
                  <Sparkles size={16} className="text-[#D4AF37]" />
                  Secure Stripe checkout
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA]">Artist name *</Label>
                  <Input value={form.artist_name} onChange={update("artist_name")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Track/EP title *</Label>
                  <Input value={form.title} onChange={update("title")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Email *</Label>
                  <Input value={form.email} onChange={update("email")} type="email" className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Phone *</Label>
                  <Input value={form.phone} onChange={update("phone")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[#A1A1AA]">Primary link *</Label>
                  <Input value={form.primary_link} onChange={update("primary_link")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-semibold text-[#FAFAFA] flex items-center gap-2">
                  <Music2 size={16} className="text-[#D4AF37]" />
                  Platform links
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label className="text-[#A1A1AA]">Spotify</Label>
                    <Input value={form.spotify} onChange={update("spotify")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Apple Music</Label>
                    <Input value={form.apple_music} onChange={update("apple_music")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">YouTube (video)</Label>
                    <Input value={form.youtube_video} onChange={update("youtube_video")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">YouTube (channel)</Label>
                    <Input value={form.youtube_channel} onChange={update("youtube_channel")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">SoundCloud</Label>
                    <Input value={form.soundcloud} onChange={update("soundcloud")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Audiomack</Label>
                    <Input value={form.audiomack} onChange={update("audiomack")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Tidal</Label>
                    <Input value={form.tidal} onChange={update("tidal")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Amazon Music</Label>
                    <Input value={form.amazon_music} onChange={update("amazon_music")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Deezer</Label>
                    <Input value={form.deezer} onChange={update("deezer")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Bandcamp</Label>
                    <Input value={form.bandcamp} onChange={update("bandcamp")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Pandora</Label>
                    <Input value={form.pandora} onChange={update("pandora")} placeholder="https://..." className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <Label className="text-[#A1A1AA]">Genre</Label>
                  <Input value={form.genre} onChange={update("genre")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Mood</Label>
                  <Input value={form.mood} onChange={update("mood")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Target audience (optional)</Label>
                  <Input value={form.target_audience} onChange={update("target_audience")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div>
                  <Label className="text-[#A1A1AA]">Location (optional)</Label>
                  <Input value={form.location} onChange={update("location")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[#A1A1AA]">Notes / goals</Label>
                  <Textarea value={form.notes} onChange={update("notes")} className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" rows={5} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Packages */}
          <div className="space-y-4">
            <Card className="bg-[#121214] border-[#27272A]">
              <CardContent className="pt-6">
                <h2 className="text-xl font-semibold text-[#FAFAFA]">Packages</h2>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  Choose a promotion + traffic campaign package.
                </p>

                <div className="space-y-3 mt-4">
                  {PACKAGES.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPackageId(p.id)}
                      className={`w-full text-left rounded-xl border px-4 py-4 transition-all ${
                        selectedPackageId === p.id
                          ? "border-[#D4AF37] bg-[#D4AF37]/5"
                          : "border-[#27272A] hover:border-[#D4AF37]/60"
                      }`}
                      data-testid={`promo-package-${p.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[#FAFAFA] font-semibold">{p.name}</p>
                          <p className="text-xs text-[#A1A1AA] mt-1">{p.duration_days} days</p>
                        </div>
                        <p className="text-[#D4AF37] font-bold">${p.price}</p>
                      </div>
                      <p className="text-xs text-[#71717A] mt-2">{p.description}</p>
                    </button>
                  ))}
                </div>

                <div className="mt-4 p-3 rounded-lg bg-[#1A1A1D] border border-[#27272A]">
                  <p className="text-xs text-[#A1A1AA]">
                    Payments handled by Stripe. Apple Pay / Google Pay / cards / Link are supported automatically
                    when available.
                  </p>
                </div>

                <Button
                  className="w-full btn-gold mt-4 py-6 text-base"
                  onClick={handleCheckout}
                  disabled={submitting}
                  data-testid="promo-checkout-btn"
                >
                  {submitting ? "Starting checkout…" : "Continue to Checkout"}
                </Button>

                <Button
                  variant="outline"
                  className="w-full mt-3 border-[#27272A] text-[#A1A1AA]"
                  onClick={() => window.open(form.primary_link || "https://dopeinhd.com", "_blank")}
                  disabled={!form.primary_link}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Preview primary link
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#121214] border-[#27272A]">
              <CardContent className="pt-6">
                <h3 className="text-sm font-semibold text-[#FAFAFA]">What you’ll see</h3>
                <ul className="text-xs text-[#A1A1AA] mt-3 space-y-2">
                  <li>• Clicks / reach / conversion-style metrics</li>
                  <li>• Campaign status updates (pending → active → complete)</li>
                  <li>• A clean smart-link hub page</li>
                </ul>
                <p className="text-xs text-[#71717A] mt-4">
                  We do not promise guaranteed streams. We run promotion + traffic campaigns to increase plays/streams.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoBoostPortal;
